import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {AbstractControl, FormBuilder, FormGroup, Validators} from "@angular/forms";
import {Router} from "@angular/router";
import {of, Subject} from "rxjs";
import {catchError, switchMap, takeUntil, tap} from "rxjs/operators";
import {MessageService} from "primeng/api";

// Services / Subjects
import {AdService} from "../../../services/ad.service";
import {CatalogService} from "../../../services/catalog.service";
import {PropertyService} from "../../../services/property.service";
import {PropertyInitialSubject} from "../../../subject/property.initial.subject";
import {FlowModeSubject} from "../../../subject/flow.mode.subject";
import {FlowService} from "../../../services/flow.service";
import {FlowStateService} from "../../../services/flow.state.service";
import {AdSubjectService} from "../../../subject/ad.subject";

// DTOs / Enums
import {PropertyInitialResponseDto} from "../../../dto/property.initial.response.dto";
import {FlowMode} from "../../../dto/enum/flow.mode.enum";
import {RecordStageEnum} from "../../../dto/enum/record.stage.enum";
import {ZoneService} from "../../../services/zone.service";
import {ZonePriceMapDialogComponent} from "../zone-price-map-dialog/zone-price-map-dialog.component";
import {FeatureService} from "../../../services/feature.service";
import {PriceService} from "../../../services/price.service";
import {PriceComponentPayload} from "../../../dto/price.payload.dto";
import {FeatureGroupDto} from "../../../dto/feature.group.dto";

const STEP_KEY = 'price';

/** Componente de precio de Venta (una fila de W_PRICE). */
interface SaleComponent {
    code: string;        // PRICE_BUILT | PRICE_GARAGE | PRICE_STORAGE | PRICE_GARDEN | PRICE_LAND
    label: string;       // etiqueta legible (del feature)
    isMain: boolean;     // PRICE_BUILT / PRICE_LAND → precio principal del inmueble
    hasCount: boolean;   // garage/baulera → se cobran por unidad (footage = cantidad)
    countLabel: string;  // "¿Cuántos garajes?" / "¿Cuántas bauleras?"
    footageM2: number;   // m² del componente (construido/terreno/jardín) si no es por cantidad
}

@Component({
    selector: 'app-property-price',
    templateUrl: './property-price.component.html',
    styleUrls: ['./property-price.component.scss']
})
export class PropertyPriceComponent implements OnInit, OnDestroy {

    // --------- Flujo / navegación -------------
    currentMode: FlowMode = FlowMode.CREATE;
    previousUrl = '';
    nextUrl = '';

    // --------- Propiedad / backend -------------
    propertyInitial: PropertyInitialResponseDto | null = null;
    propertyId?: number;
    selectedCityId!: number;
    selectedSegmentId!: number;
    selectedAdId!: number;
    selectedZoneId!: number;

    // --------- UI / estado -------------
    loading = true;
    priceForm!: FormGroup;
    selectedOffer: { adId: number, adCode: string, adLabel?: string } | null = null;
    periodOptions: { optionCode: string, optionName: string }[] = [];
    hadExistingAd = false; // decide POST vs PUT
    isAdditionalOffer = false; // viene de /ad: oferta extra → no re-avanzar la máquina de etapas
    private destroy$ = new Subject<void>();
    zonePrice: number | null = null;
    zonePriceUnavailable = false; // no hay referencia en W_ZONE_PRICE para esta zona/oferta/segmento
    showPriceHelp = false; // popup de ayuda "?" (Información sobre el precio)

    // --------- Venta: desglose por componente (W_PRICE) -------------
    saleComponents: SaleComponent[] = [];
    saleForm!: FormGroup; // controles dinámicos price__<code> y count__<code>

    // --------- UI dinámico -------------
    segmentDisplay: string | undefined = '';
    offerDisplay: string | undefined = '';
    currencyPrefix = 'USD';
    currencyName = 'dólares americanos';
    pricePlaceholder = '500,000.00 USD';
    @ViewChild('priceDialog') priceDialog!: ZonePriceMapDialogComponent;


    constructor(
        private fb: FormBuilder,
        private router: Router,
        private adService: AdService,
        private catalogService: CatalogService,
        private propertyService: PropertyService,
        private propertyInitialSubject: PropertyInitialSubject,
        private flowModeSubject: FlowModeSubject,
        private flowService: FlowService,
        private flowState: FlowStateService,
        private adSubject: AdSubjectService,
        private message: MessageService,
        private zoneService: ZoneService,
        private featureService: FeatureService,
        private priceService: PriceService
    ) {}

    ngOnInit(): void {
        // 1) Modo y rutas
        const mode = this.flowModeSubject.getCurrentMode();
        if (!mode) { this.router.navigate(['/list-pending']); return; }
        this.currentMode = mode;
        this.previousUrl = this.flowService.getPreviousStep(STEP_KEY);
        this.nextUrl = this.flowService.getNextStep(STEP_KEY);

        // 2) Paso actual
        this.flowState.setCurrentStep(STEP_KEY);

        // 3) Propiedad y stage
        this.propertyInitial = this.propertyInitialSubject.getCurrentProperty();
        if (!this.propertyInitial?.propertyId) { this.router.navigate(['/list-pending']); return; }
        this.propertyId = this.propertyInitial.propertyId;
        if (this.propertyInitial.recordStage) {
            this.flowState.setRecordStage(this.propertyInitial.recordStage as RecordStageEnum);
        }
        this.flowState.setPropertyId(this.propertyId);

        // 4) Resolver oferta seleccionada (desde FlowState/adSubject/backend)
        this.resolveSelectedOffer().subscribe(() => {
            // 5) Inicializa form, carga periodos y aplica cache
            this.setCurrencyForOffer();
            this.initForm();
            this.loadPeriodOptionsIfNeeded();
            this.loadSaleComponents();
            this.applyCache();
            this.getZonePrice();
            this.loading = false;
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    // -----------------------------------------------------------
    // Resolver oferta seleccionada
    // -----------------------------------------------------------
    private resolveSelectedOffer() {
        // a) FlowState del paso 'ad'
        const adCache = this.flowState.getStepData('ad');
        this.isAdditionalOffer = !!adCache?.isAdditionalOffer;
        if (adCache?.selectedAdId && adCache?.selectedAdCode) {
            this.hadExistingAd = !!adCache.selectedIsRegistered; // oferta ya registrada → PUT, no POST
            this.selectedOffer = {
                adId: adCache.selectedAdId,
                adCode: adCache.selectedAdCode,
                adLabel: adCache.selectedAdLabel
            };
            this.offerDisplay = this.mapOfferDisplay(this.selectedOffer.adCode, this.selectedOffer.adLabel);
            return of(true);
        }

        // b) AdSubject (fallback)
        const adSubjectOffer = this.adSubject.getSelectedOffer?.();
        if (adSubjectOffer?.adId) {
            this.selectedOffer = {
                adId: adSubjectOffer.adId,
                adCode: adSubjectOffer.adCode,
                adLabel: adSubjectOffer.adLabel
            };
            this.offerDisplay = this.mapOfferDisplay(this.selectedOffer.adCode, this.selectedOffer.adLabel);
            return of(true);
        }

        // c) Backend: oferta existente (para EDIT/RESUME)
        if (!this.propertyId) return of(false);
        return this.adService.getOffersListProperty(this.propertyId).pipe(
            tap((ads: any[]) => {
                if (Array.isArray(ads) && ads.length) {
                    const a = ads[0];
                    this.selectedOffer = { adId: a.adId, adCode: a.adCode, adLabel: a.adLabel };
                    this.hadExistingAd = true;
                    this.offerDisplay = this.mapOfferDisplay(this.selectedOffer.adCode, this.selectedOffer.adLabel);
                } else {
                    this.message.add({ severity: 'warn', summary: 'Anuncio', detail: 'No hay tipo de anuncio seleccionado.' });
                    this.router.navigate(['/ad']);
                }
            }),
            catchError(() => { this.router.navigate(['/ad']); return of(false); })
        );
    }


    // -----------------------------------------------------------
    // Form + validaciones de dinero
    // -----------------------------------------------------------
    private initForm(): void {
        this.priceForm = this.fb.group({
            price: ['', [Validators.required, this.moneyValidator]],
            period: [''],     // requerido sólo si RENT/LEASING
            adDate: [this.getTodayDate(), Validators.required]
        });

        // Etiquetas de UI
        this.segmentDisplay = this.propertyInitial?.segmentLabel;
    }

    moneyValidator = (control: AbstractControl) => {
        const raw = (control.value ?? '').toString().trim();
        if (!raw) return null; // vacío = válido; la obligatoriedad (precio principal) se valida aparte
        const num = this.unformatMoney(raw);
        if (isNaN(num) || num < 0) return { money: true };
        return null;
    };

    onPriceTyping(evt: Event) {
        const el = evt.target as HTMLInputElement;
        el.value = el.value.replace(/[^\d.,]/g, ''); // sólo números, coma, punto
        this.priceForm.get('price')?.setValue(el.value, { emitEvent: false });
    }

    unformatPrice() {
        const c = this.priceForm.get('price'); if (!c) return;
        const num = this.unformatMoney(c.value);
        c.setValue(isNaN(num) ? '' : this.toPlain(num), { emitEvent: false });
    }

    formatPrice() {
        const c = this.priceForm.get('price'); if (!c) return;
        const num = this.unformatMoney(c.value);
        if (isNaN(num)) return;
        c.setValue(this.formatMoney(num) + ' ' + this.currencyPrefix, { emitEvent: false });
        c.markAsTouched();
    }

    /** Bolivianos usa separadores es-BO (1.234,56); USD usa en-US (1,234.56). */
    get isBsCurrency(): boolean { return this.currencyPrefix === 'Bs'; }

    private unformatMoney(v: any): number {
        if (v == null) return NaN;
        let s = String(v).replace(/[^\d.,]/g, '');
        if (!s) return NaN;
        if (this.isBsCurrency) {
            s = s.replace(/\./g, '').replace(',', '.'); // es-BO: "." miles, "," decimal
        } else {
            s = s.replace(/,/g, '');                    // en-US: "," miles, "." decimal
        }
        return parseFloat(s);
    }
    /** Número plano editable (sin miles), con el separador decimal de la moneda. */
    private toPlain(n: number): string {
        const fixed = (Math.round(n * 100) / 100).toString(); // siempre con "." decimal
        return this.isBsCurrency ? fixed.replace('.', ',') : fixed;
    }
    private formatMoney(n: number): string {
        return n.toLocaleString(this.isBsCurrency ? 'es-BO' : 'en-US',
            { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }

    /** Total de Venta formateado según la moneda (para el template, sin depender del locale de Angular). */
    get saleTotalDisplay(): string { return this.formatMoney(this.saleTotal); }

    /** Precio de zona formateado (entero, sin decimales forzados). */
    get zonePriceDisplay(): string {
        if (this.zonePrice == null) return '';
        return this.zonePrice.toLocaleString(this.isBsCurrency ? 'es-BO' : 'en-US',
            { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    }

    get isSale(): boolean {
        const code = this.selectedOffer?.adCode;
        return code === 'SALE' || code === 'SELL';
    }

    get isRent(): boolean {
        return this.selectedOffer?.adCode === 'RENT';
    }

    /** Venta y Alquiler usan el mismo desglose por componente; Alquiler además lleva temporalidad. */
    get hasBreakdown(): boolean {
        return this.isSale || this.isRent;
    }

    get isRentOrLeasing(): boolean {
        const code = this.selectedOffer?.adCode;
        return code === 'RENT' || code === 'LEASING';
    }

    /** Total de Venta = principal + Σ(precio_unitario × cantidad) de los extras. */
    get saleTotal(): number {
        if (!this.saleForm) return 0;
        return this.saleComponents.reduce((acc, c) => {
            const unit = this.unformatMoney(this.saleForm.get('price__' + c.code)?.value);
            if (isNaN(unit) || unit <= 0) return acc;
            const qty = c.hasCount ? (this.saleForm.get('count__' + c.code)?.value || 0) : 1;
            return acc + unit * qty;
        }, 0);
    }

    /** Cantidad actual de un componente con contador. */
    countOf(c: SaleComponent): number {
        return this.saleForm?.get('count__' + c.code)?.value || 0;
    }

    incCount(c: SaleComponent): void {
        const ctrl = this.saleForm?.get('count__' + c.code);
        if (ctrl) ctrl.setValue((ctrl.value || 0) + 1);
    }

    decCount(c: SaleComponent): void {
        const ctrl = this.saleForm?.get('count__' + c.code);
        if (ctrl && (ctrl.value || 0) > 0) ctrl.setValue(ctrl.value - 1);
    }

    /** Formatea/limpia el input de precio de un componente de Venta. */
    onSalePriceBlur(c: SaleComponent): void {
        const ctrl = this.saleForm?.get('price__' + c.code); if (!ctrl) return;
        const num = this.unformatMoney(ctrl.value);
        ctrl.setValue(isNaN(num) ? '' : this.formatMoney(num) + ' ' + this.currencyPrefix, { emitEvent: false });
    }
    onSalePriceFocus(c: SaleComponent): void {
        const ctrl = this.saleForm?.get('price__' + c.code); if (!ctrl) return;
        const num = this.unformatMoney(ctrl.value);
        ctrl.setValue(isNaN(num) ? '' : this.toPlain(num), { emitEvent: false });
    }
    onSalePriceInput(evt: Event, c: SaleComponent): void {
        const el = evt.target as HTMLInputElement;
        el.value = el.value.replace(/[^\d.,]/g, ''); // sólo números, coma y punto
        this.saleForm?.get('price__' + c.code)?.setValue(el.value, { emitEvent: false });
    }
    get needPeriod(): boolean { return this.isRentOrLeasing; }
    get priceInvalid(): boolean {
        const c = this.priceForm?.get('price');
        return !!(c && c.touched && c.invalid);
    }

    // -----------------------------------------------------------
    // Catálogo de periodos (sólo cuando aplica)
    // -----------------------------------------------------------
    private loadPeriodOptionsIfNeeded(): void {
        if (!this.selectedOffer?.adCode) return;
        const cat = this.selectedOffer.adCode === 'RENT' ? 'CT_RENT_PERIOD'
            : this.selectedOffer.adCode === 'LEASING' ? 'CT_LEASING_PERIOD'
                : null;
        if (!cat) return;

        this.catalogService.getCatalogList(cat).pipe(
            tap((data: any[]) => {
                this.periodOptions = (data || []).map(d => ({ optionCode: d.optionCode, optionName: d.optionName }));
            }),
            catchError(() => of([])),
            takeUntil(this.destroy$)
        ).subscribe();
    }

    // -----------------------------------------------------------
    // Venta: componentes de precio (grupo PRICE del segmento) → W_PRICE
    // -----------------------------------------------------------
    private loadSaleComponents(): void {
        if (!this.hasBreakdown) return; // Venta y Alquiler usan el desglose; Anticrético no
        const specs = this.saleComponentSpecsForSegment();
        if (!specs.length) { this.saleComponents = []; return; }
        // Qué componentes pide cada segmento es REGLA DE NEGOCIO (no toda la BD): Departamento lleva
        // construcción + garaje + baulera; el resto sólo su precio principal; Terreno sólo terreno.
        // El fetch de features primary sirve sólo para resolver los m² (footage) del componente principal.
        this.featureService.getFeaturePrimaryListByPropertyId(this.propertyId).pipe(
            catchError(() => of([])),
            takeUntil(this.destroy$)
        ).subscribe((primary: FeatureGroupDto[]) => {
            const all = primary || [];
            this.saleComponents = specs.map(s => ({
                code: s.code,
                label: s.label,
                isMain: s.isMain,
                hasCount: s.hasCount,
                countLabel: s.countLabel,
                footageM2: s.m2Code ? this.findFeatureValueNumber(all, s.m2Code) : 0
            }));
            this.buildSaleForm();
            this.applySaleCache();
            this.loadSavedPrices(); // EDIT/RESUME: hidrata el form con lo guardado en W_PRICE
        });
    }

    /** Componentes de precio por segmento (regla de negocio acordada con el usuario). */
    private saleComponentSpecsForSegment(): { code: string; label: string; isMain: boolean; hasCount: boolean; countLabel: string; m2Code: string | null }[] {
        const main = (code: string, label: string, m2Code: string | null) =>
            ({ code, label, isMain: true, hasCount: false, countLabel: '', m2Code });
        switch (this.propertyInitial?.segmentId) {
            case 2: // Departamento → construcción + garaje + baulera
                return [
                    main('PRICE_BUILT', 'Precio Departamento', 'BUILT_M2'),
                    { code: 'PRICE_GARAGE',  label: 'Precio Garaje',  isMain: false, hasCount: true, countLabel: '¿Cuántos garajes?',  m2Code: null },
                    { code: 'PRICE_STORAGE', label: 'Precio Baulera', isMain: false, hasCount: true, countLabel: '¿Cuántas bauleras?', m2Code: null }
                ];
            case 1: return [main('PRICE_BUILT', 'Precio Casa',     'BUILT_M2')];
            case 3: return [main('PRICE_BUILT', 'Precio Local',    'BUILT_M2')];
            case 4: return [main('PRICE_BUILT', 'Precio Oficina',  'BUILT_M2')];
            case 6: return [main('PRICE_BUILT', 'Precio Edificio', 'BUILT_M2')];
            case 7: return [main('PRICE_BUILT', 'Precio Galpón',   'BUILT_M2')];
            case 8: return [main('PRICE_BUILT', 'Precio Parqueo',  'BUILT_M2')];
            case 5: return [main('PRICE_LAND',  'Precio Terreno',  'LAND_M2')];
            default: return [main('PRICE_BUILT', 'Precio', 'BUILT_M2')];
        }
    }

    /** EDIT/RESUME: trae el desglose ya guardado (W_PRICE) y rellena los controles vacíos. */
    private loadSavedPrices(): void {
        if (!this.propertyId || !this.saleForm) return;
        this.priceService.getPricesByProperty(this.propertyId).pipe(
            catchError(() => of([])),
            takeUntil(this.destroy$)
        ).subscribe((rows: any[]) => {
            (rows || []).forEach(r => {
                if (!r?.price_code || r.price_code === 'PRICE_TOTAL') return;
                // sólo el tipo de oferta actual (una propiedad puede tener Venta y Alquiler en W_PRICE)
                if (r.ct_price_type && this.selectedOffer?.adCode && r.ct_price_type !== this.selectedOffer.adCode) return;
                const pc = this.saleForm.get('price__' + r.price_code);
                if (pc && r.price != null && (pc.value === '' || pc.value == null)) {
                    pc.setValue(this.formatMoney(Number(r.price)) + ' ' + this.currencyPrefix);
                }
                if (r.price_code === 'PRICE_GARAGE' || r.price_code === 'PRICE_STORAGE') {
                    const cc = this.saleForm.get('count__' + r.price_code);
                    if (cc && r.footage != null) cc.setValue(Number(r.footage));
                }
            });
        });
    }

    private findFeatureValueNumber(groups: FeatureGroupDto[], code: string): number {
        for (const g of groups || []) {
            const f = (g.featureList || []).find(x => x.featureCode === code);
            if (f) {
                const raw = f.decimalNumber ?? f.wholeNumber ?? f.value;
                const n = typeof raw === 'number' ? raw : parseFloat(String(raw ?? ''));
                return isNaN(n) ? 0 : n;
            }
        }
        return 0;
    }

    private buildSaleForm(): void {
        const controls: { [k: string]: any } = {};
        this.saleComponents.forEach(c => {
            controls['price__' + c.code] = ['', [this.moneyValidator]];
            if (c.hasCount) controls['count__' + c.code] = [1];
        });
        this.saleForm = this.fb.group(controls);
    }

    private applySaleCache(): void {
        const cached = this.flowState.getStepData(STEP_KEY);
        if (!cached?.saleComponents || !this.saleForm) return;
        (cached.saleComponents as any[]).forEach(sc => {
            const pc = this.saleForm.get('price__' + sc.code);
            if (pc && sc.price != null) pc.setValue(sc.price);
            const cc = this.saleForm.get('count__' + sc.code);
            if (cc && sc.count != null) cc.setValue(sc.count);
        });
    }

    /** Construye las filas de W_PRICE a partir del desglose de Venta. */
    private buildWPricePayload(): PriceComponentPayload[] {
        const type = this.selectedOffer?.adCode || 'SALE';
        const rows: PriceComponentPayload[] = [];
        this.saleComponents.forEach(c => {
            const unit = this.unformatMoney(this.saleForm.get('price__' + c.code)?.value);
            if (isNaN(unit) || unit <= 0) return; // sólo componentes con precio
            const qty = c.hasCount ? (this.saleForm.get('count__' + c.code)?.value || 0) : 1;
            if (c.hasCount && qty <= 0) return;
            rows.push({
                property_id: this.propertyId!,
                price_code: c.code,
                price_label: this.trim30(c.label),
                price: unit,
                footage: c.hasCount ? qty : (c.footageM2 || 0),
                ct_price_type: type
            });
        });
        rows.push({ // total para lectura directa
            property_id: this.propertyId!,
            price_code: 'PRICE_TOTAL',
            price_label: 'Precio total',
            price: this.saleTotal,
            footage: 0,
            ct_price_type: type
        });
        return rows;
    }

    private trim30(s: string): string { return (s || '').length > 30 ? s.substring(0, 30) : (s || ''); }

    private validateSale(): boolean {
        const main = this.saleComponents.find(c => c.isMain);
        if (!main) {
            this.message.add({ severity: 'warn', summary: 'Validación', detail: 'No hay componente de precio principal.' });
            return false;
        }
        const mainPrice = this.unformatMoney(this.saleForm.get('price__' + main.code)?.value);
        if (isNaN(mainPrice) || mainPrice <= 0) {
            this.saleForm.get('price__' + main.code)?.markAsTouched();
            this.message.add({ severity: 'warn', summary: 'Validación', detail: 'Ingresa el precio principal.' });
            return false;
        }
        // Componentes con cantidad: si hay 1 o más, su precio es obligatorio (0 = no lo ofrece, puede ir vacío).
        for (const c of this.saleComponents) {
            if (!c.hasCount) continue;
            const count = this.saleForm.get('count__' + c.code)?.value || 0;
            const price = this.unformatMoney(this.saleForm.get('price__' + c.code)?.value);
            if (count >= 1 && (isNaN(price) || price <= 0)) {
                this.saleForm.get('price__' + c.code)?.markAsTouched();
                const noun = c.label.replace(/^Precio\s+/i, '').toLowerCase();
                this.message.add({ severity: 'warn', summary: 'Validación', detail: `Tienes ${count} ${noun}: ingresa su precio.` });
                return false;
            }
        }
        // Alquiler: la temporalidad (cada cuánto se paga) es obligatoria.
        if (this.isRent && !this.priceForm.get('period')?.value) {
            this.message.add({ severity: 'warn', summary: 'Validación', detail: 'Selecciona cada cuánto se paga el alquiler.' });
            return false;
        }
        if (this.saleForm.invalid) {
            this.message.add({ severity: 'warn', summary: 'Validación', detail: 'Revisa los precios ingresados.' });
            return false;
        }
        return true;
    }

    // -----------------------------------------------------------
    // Cache local (FlowState)
    // -----------------------------------------------------------
    private applyCache(): void {
        const cached = this.flowState.getStepData(STEP_KEY);
        if (!cached) return;

        const { adPrice, adDate, ctRentPeriod, ctLeasingPeriod } = cached;
        if (adPrice != null)  this.priceForm.get('price')?.setValue(adPrice);
        if (adDate)          this.priceForm.get('adDate')?.setValue(adDate);

        if (this.selectedOffer?.adCode === 'RENT' && ctRentPeriod)       this.priceForm.get('period')?.setValue(ctRentPeriod);
        if (this.selectedOffer?.adCode === 'LEASING' && ctLeasingPeriod) this.priceForm.get('period')?.setValue(ctLeasingPeriod);
    }

    private persistToFlowState(): void {
        const raw = this.priceForm.getRawValue();
        const data: any = {
            adPrice: this.hasBreakdown ? String(this.saleTotal) : raw.price,
            adDate: raw.adDate,
            ctRentPeriod: this.selectedOffer?.adCode === 'RENT' ? raw.period : null,
            ctLeasingPeriod: this.selectedOffer?.adCode === 'LEASING' ? raw.period : null
        };
        if (this.hasBreakdown && this.saleForm) {
            data.saleComponents = this.saleComponents.map(c => ({
                code: c.code,
                price: this.saleForm.get('price__' + c.code)?.value,
                count: c.hasCount ? this.saleForm.get('count__' + c.code)?.value : undefined
            }));
        }
        this.flowState.setStepData(STEP_KEY, data);
    }

    // -----------------------------------------------------------
    // Guardado (POST/PUT /ad/{propertyId})
    // -----------------------------------------------------------
    private buildPayload(): any {
        const raw = this.priceForm.getRawValue();
        const priceNumber = this.hasBreakdown ? this.saleTotal : this.unformatMoney(raw.price);

        const payload: any = {
            propertyId: this.propertyId,
            adId: this.selectedOffer?.adId,
            adPrice: String(priceNumber),
            adDate: raw.adDate
        };
        if (this.selectedOffer?.adCode === 'RENT')     payload.ctRentPeriod     = raw.period || null;
        if (this.selectedOffer?.adCode === 'LEASING')  payload.ctLeasingPeriod  = raw.period || null;

        Object.keys(payload).forEach(k => payload[k] === undefined && delete payload[k]);
        return payload;
    }

    private savePrice$() {
        if (!this.propertyId || !this.selectedOffer) return of(null);
        this.persistToFlowState();

        const payload = this.buildPayload();
        const adReq$ = this.hadExistingAd
            ? this.adService.updateOfferToProperty(this.propertyId, payload) // PUT /ad/{propertyId}
            : this.adService.saveOfferToProperty(this.propertyId, payload);  // POST /ad/{propertyId}

        // Venta/Alquiler: además del AD (total + temporalidad), persistimos el desglose en W_PRICE.
        const priceReq$ = (this.hasBreakdown && this.propertyId)
            ? this.priceService.registerPropertyPrices(this.propertyId, this.buildWPricePayload())
            : of(null);

        return adReq$.pipe(
            switchMap(() => priceReq$),
            tap(() => this.flowState.setRecordStage(RecordStageEnum.PUBLISH)), // tu mapeo actual: PUBLISH -> 'price'
            catchError((err) => {
                console.error('Error guardando precio', err);
                this.message.add({ severity: 'error', summary: 'Error', detail: 'No se pudo guardar el precio.' });
                return of(null);
            })
        );
    }

    private shouldAdvanceStage(): boolean {
        if (this.currentMode === FlowMode.EDIT) return false;
        if (this.currentMode === FlowMode.CREATE) return true;

        // RESUME → avanza sólo si current > recordStage
        const stage = this.flowState.recordStage;
        if (!stage) return false;

        const stageStep = this.flowState.getStepByRecordStage(stage);
        const currentIdx = this.flowService.getStepIndex(STEP_KEY);
        const stageIdx = this.flowService.getStepIndex(stageStep);
        if (currentIdx === -1 || stageIdx === -1) return false;

        return currentIdx > stageIdx;
    }

    // -----------------------------------------------------------
    // Botonera
    // -----------------------------------------------------------
    handleNextStep(): void {
        if (this.hasBreakdown) {
            if (!this.validateSale()) return;
        } else {
            if (this.priceForm.invalid) {
                this.message.add({ severity: 'warn', summary: 'Validación', detail: 'Ingresa un precio válido.' });
                return;
            }
            if (this.needPeriod && !this.priceForm.get('period')?.value) {
                this.message.add({ severity: 'warn', summary: 'Validación', detail: 'Selecciona una frecuencia.' });
                return;
            }
        }

        this.savePrice$().pipe(
            switchMap(() => {
                // Oferta adicional: la propiedad ya pasó la etapa de precio → no la re-avanzamos.
                if (!this.isAdditionalOffer && this.shouldAdvanceStage()) {
                    const body = { id: this.propertyId };
                    return this.propertyService.nextStageProperty(body).pipe(
                        catchError((err) => { console.error('nextStage error', err); return of(null); })
                    );
                }
                return of(null);
            }),
            takeUntil(this.destroy$)
        ).subscribe(() => this.router.navigate(['/ad'])); // volver al hub de ofertas (agregar otra o Concluir)
    }

    handleSaveAndExit(): void {
        if (this.hasBreakdown) {
            if (!this.validateSale()) return;
        } else if (this.priceForm.invalid) {
            this.message.add({ severity: 'warn', summary: 'Validación', detail: 'Ingresa un precio válido.' });
            return;
        }
        this.savePrice$().pipe(
            catchError(() => of(null)),
            takeUntil(this.destroy$)
        ).subscribe(() => this.router.navigate(['/list-pending']));
    }

    handlePreviousStep(): void {
        this.persistToFlowState();
        this.router.navigate([this.previousUrl]);
    }

    // -----------------------------------------------------------
    // Utils
    // -----------------------------------------------------------
    private getTodayDate(): string {
        const d = new Date();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        return `${d.getFullYear()}-${mm}-${dd}`;
    }

    openPriceHelp() { this.showPriceHelp = true; }

    openZoneMap() {
        if (!this.propertyId) return;
        if (this.selectedCityId && this.selectedSegmentId && this.selectedAdId) {
            this.priceDialog.openDialog(); // ✅ Aquí ya están listos los inputs
        }
    }

    onPolicies() {
    }

    getZonePrice() {
        this.propertyService.getPropertyLocation(this.propertyId).subscribe(loc => {
            console.log('Property location', loc);
            this.selectedCityId = loc?.cityId;
            this.selectedSegmentId = this.propertyInitial?.segmentId!;
            this.selectedAdId = this.selectedOffer?.adId!;
            this.selectedZoneId = loc?.zoneId!;
            console.log('Cargando zona precio', this.selectedZoneId, this.selectedSegmentId, this.selectedAdId);
            this.zoneService.getZonePrice(this.selectedZoneId, this.selectedSegmentId, this.selectedAdId)
                .pipe(takeUntil(this.destroy$))
                .subscribe({
                    next: (price) => {
                        const raw = price?.price ?? null;
                        const num = raw == null ? NaN : Number(raw);
                        this.zonePrice = (!isNaN(num) && num > 0) ? num : null;
                        this.zonePriceUnavailable = this.zonePrice == null;
                    },
                    error: () => { this.zonePrice = null; this.zonePriceUnavailable = true; }
                });
        });
    }

    /** Moneda según la oferta (Figma): Venta en Bolivianos, Alquiler/Anticrético en dólares. */
    private setCurrencyForOffer(): void {
        if (this.isSale) {
            this.currencyPrefix = 'Bs';
            this.currencyName = 'Bolivianos';
            this.pricePlaceholder = '200.000,00 Bs';
        } else {
            this.currencyPrefix = 'USD';
            this.currencyName = 'dólares americanos';
            this.pricePlaceholder = '500,000.00 USD';
        }
    }

    private mapOfferDisplay(code?: string, label?: string): string {
        if (label) return label;
        switch (code) {
            case 'SALE':
            case 'SELL':     return 'Venta';
            case 'RENT':     return 'Alquiler';
            case 'LEASING':  return 'Leasing';
            case 'ANTICRETIC':
            case 'ANTICRETICO': return 'Anticrético';
            default:         return '';
        }
    }

}
